var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useRef } from "react";
import ReactECharts from "echarts-for-react";
import FunnelGraphComponent from "./funnel";
function isTwoDimensionalArray(arr) {
    return Array.isArray(arr) && arr.length > 0 && Array.isArray(arr[0]);
}
function Chart(_a) {
    var _b;
    var _c = _a.chartType, chartType = _c === void 0 ? "bar" : _c, chartData = _a.chartData, _d = _a.symbol, symbol = _d === void 0 ? ["$", "K"] : _d;
    var rootStyles = getComputedStyle(document.documentElement);
    var labelColor = rootStyles.getPropertyValue("--colors-label").trim();
    var bgColor = rootStyles.getPropertyValue("--colors-body-bg-sec").trim();
    if (!symbol) {
        symbol = ["$", "K"];
    }
    var chartRef = useRef(null);
    var colors = (chartData === null || chartData === void 0 ? void 0 : chartData.colors) || [];
    var getChartBarOption = function () {
        var _a, _b;
        var groupedData = {};
        (_a = chartData === null || chartData === void 0 ? void 0 : chartData.labels) === null || _a === void 0 ? void 0 : _a.forEach(function (label, index) {
            if (!groupedData[label]) {
                groupedData[label] = [];
            }
            groupedData[label].push({
                value: chartData.data[index],
                tooltip: chartData.tooltips[index],
                color: chartData.colors[index],
                key: chartData.key[index],
            });
        });
        var uniqueLabels = Object.keys(groupedData);
        var formattedKeys = (_b = chartData === null || chartData === void 0 ? void 0 : chartData.key) === null || _b === void 0 ? void 0 : _b.map(function (key) { return key || "Unknown"; });
        var series = formattedKeys === null || formattedKeys === void 0 ? void 0 : formattedKeys.map(function (key, keyIndex) { return ({
            name: key,
            type: "bar",
            stack: "total",
            /*data: uniqueLabels.map((label) => {
              const item = groupedData[label].find((item) => item.key === key);
              return item ? item.value : 0;
            }),*/
            data: uniqueLabels.map(function (label) {
                var _a;
                var item = groupedData[label].find(function (item) { return item.key === key; });
                var module = (_a = chartData.modules) === null || _a === void 0 ? void 0 : _a[keyIndex];
                if (item) {
                    return module ? { value: item.value, module: module } : item.value; // Just return value if module doesn't exist
                }
                return module ? { value: 0, module: module } : 0; // Same logic for missing data
            }),
            itemStyle: {
                color: chartData.colors[keyIndex],
            },
            label: {
                show: true,
                position: "inside",
                formatter: function (param) {
                    var _a;
                    return param.value > 0
                        ? ((_a = param.data) === null || _a === void 0 ? void 0 : _a.modules)
                            ? "".concat(param.data.modules, "\n$").concat(param.value).concat(symbol[1])
                            : "".concat(symbol[0]).concat(param.value).concat(symbol[1])
                        : "";
                },
                color: "#fff",
                fontWeight: "bold",
            },
            emphasis: {
                label: { show: true },
            },
        }); });
        var isHorizontal = chartType === "horizontalBar";
        return {
            tooltip: {
                trigger: "item",
                formatter: function (param) {
                    var _a;
                    var label = param.name;
                    var seriesIndex = param.seriesIndex;
                    var dataPoint = (_a = groupedData[label]) === null || _a === void 0 ? void 0 : _a.find(function (item) { return item.key === chartData.key[seriesIndex]; });
                    if (!dataPoint)
                        return "".concat(param.seriesName, ": ").concat(param.value).concat(symbol[1]);
                    return "".concat(dataPoint.tooltip);
                },
            },
            legend: {
                show: true,
                data: chartData.key,
                orient: "vertical",
                right: "right",
                top: "top",
                formatter: function (name) {
                    var index = formattedKeys.indexOf(name);
                    if (index !== -1) {
                        return "".concat(name, " (").concat(symbol[0]).concat(chartData.data[index]).concat(symbol[1], ")");
                    }
                    return name;
                },
                backgroundColor: "rgba(113, 121, 126, 0.7)",
                borderColor: bgColor,
                borderWidth: 1,
                borderRadius: 8,
                textStyle: {
                    color: "#fff",
                    fontSize: 12,
                },
            },
            grid: {
                left: "5%",
                right: "5%",
                top: "10%",
                bottom: "5%",
                containLabel: true,
            },
            xAxis: isHorizontal
                ? {
                    type: "value",
                    axisLine: { lineStyle: { color: labelColor } },
                    axisLabel: {
                        color: labelColor,
                        formatter: function (value) { return "".concat(symbol[0]).concat(value).concat(symbol[1]); },
                    },
                }
                : {
                    type: "category",
                    data: uniqueLabels,
                    axisLine: { lineStyle: { color: labelColor } },
                    axisLabel: {
                        color: labelColor,
                        formatter: function (value) {
                            return value.length > 10 ? value.slice(0, 10) + "..." : value;
                        },
                    },
                },
            yAxis: isHorizontal
                ? {
                    type: "category",
                    data: uniqueLabels,
                    axisLine: { lineStyle: { color: labelColor } },
                    axisLabel: {
                        color: labelColor,
                        formatter: function (value) {
                            return value.length > 10 ? value.slice(0, 10) + "..." : value;
                        },
                    },
                }
                : {
                    type: "value",
                    axisLine: { lineStyle: { color: labelColor } },
                    axisLabel: {
                        color: labelColor,
                        formatter: function (value) { return "".concat(symbol[0]).concat(value).concat(symbol[1]); },
                    },
                },
            series: series,
        };
    };
    var getChartOption = function () {
        var _a;
        return {
            tooltip: {
                trigger: "item",
                formatter: function (params) {
                    var _a;
                    var dataIndex = params.dataIndex;
                    return ((_a = chartData === null || chartData === void 0 ? void 0 : chartData.tooltips) === null || _a === void 0 ? void 0 : _a[dataIndex]) || "";
                },
            },
            legend: chartType === "pie" ||
                chartType === "funnel" ||
                chartType === "doughnut"
                ? {
                    orient: "vertical",
                    left: "left",
                    top: "top",
                    textStyle: {
                        color: labelColor,
                        fontSize: 12,
                    },
                    itemGap: 5,
                    formatter: function (name) {
                        var index = chartData === null || chartData === void 0 ? void 0 : chartData.labels.indexOf(name);
                        return "".concat(name, " (").concat(chartData === null || chartData === void 0 ? void 0 : chartData.data[index]).concat(symbol[1], ")");
                    },
                }
                : chartType === "bar" || chartType === "line"
                    ? {
                        orient: "horizontal",
                        left: "center",
                        top: "bottom",
                        textStyle: {
                            color: labelColor,
                            fontSize: 12,
                        },
                        itemGap: 10,
                        formatter: function (name) {
                            var index = chartData === null || chartData === void 0 ? void 0 : chartData.labels.indexOf(name);
                            return "".concat(name, " (").concat(chartData === null || chartData === void 0 ? void 0 : chartData.data[index]).concat(symbol[1], ")");
                        },
                    }
                    : undefined,
            grid: {
                left: "5%",
                right: "5%",
                top: "5%",
                bottom: "5%",
                containLabel: true,
            },
            xAxis: chartType === "horizontalBar"
                ? {
                    type: "value",
                    show: true,
                    axisLine: {
                        lineStyle: {
                            color: labelColor,
                        },
                    },
                    axisLabel: {
                        color: labelColor,
                        formatter: function (value) {
                            return "".concat(symbol[0]).concat(value).concat(symbol[1]);
                        },
                    },
                }
                : chartType === "bar" || chartType === "line"
                    ? {
                        type: "category",
                        data: chartData === null || chartData === void 0 ? void 0 : chartData.labels,
                        show: true,
                        axisLine: {
                            lineStyle: {
                                color: labelColor,
                            },
                        },
                        axisLabel: {
                            color: labelColor,
                            formatter: function (value) {
                                return value.length > 10 ? value.slice(0, 10) + "..." : value;
                            },
                        },
                    }
                    : {
                        show: false,
                    },
            yAxis: chartType === "horizontalBar"
                ? {
                    type: "category",
                    data: chartData === null || chartData === void 0 ? void 0 : chartData.labels,
                    show: true,
                    axisLine: {
                        lineStyle: {
                            color: labelColor,
                        },
                    },
                    axisLabel: {
                        color: labelColor,
                    },
                }
                : chartType === "bar" || chartType === "line"
                    ? {
                        type: "value",
                        show: true,
                        axisLine: {
                            lineStyle: {
                                color: labelColor,
                            },
                        },
                        axisLabel: {
                            color: labelColor,
                            formatter: function (value) {
                                return "".concat(symbol[0]).concat(value).concat(symbol[1]);
                            },
                        },
                    }
                    : {
                        show: false,
                    },
            series: [
                {
                    type: chartType === "horizontalBar"
                        ? "bar"
                        : chartType === "doughnut"
                            ? "pie"
                            : chartType,
                    radius: chartType === "doughnut" ? ["40%", "70%"] : "70%",
                    stack: "total",
                    data: chartType === "pie" ||
                        chartType === "doughnut" ||
                        chartType === "funnel" ||
                        chartType === "horizontalBar" ||
                        chartType === "bar"
                        ? (_a = chartData === null || chartData === void 0 ? void 0 : chartData.labels) === null || _a === void 0 ? void 0 : _a.map(function (category, index) {
                            var _a, _b;
                            var base = {
                                value: (_a = chartData === null || chartData === void 0 ? void 0 : chartData.data) === null || _a === void 0 ? void 0 : _a[index],
                                name: category,
                                itemStyle: {
                                    color: colors[index % colors.length],
                                },
                            };
                            var module = (_b = chartData === null || chartData === void 0 ? void 0 : chartData.modules) === null || _b === void 0 ? void 0 : _b[index];
                            return module ? __assign(__assign({}, base), { module: module }) : base;
                        })
                        : chartData === null || chartData === void 0 ? void 0 : chartData.data,
                    smooth: chartType === "line",
                    label: {
                        show: chartType === "horizontalBar" ||
                            chartType === "bar" ||
                            chartType === "pie" ||
                            chartType === "doughnut",
                        formatter: chartType === "pie" ||
                            chartType === "funnel" ||
                            chartType === "doughnut"
                            ? "{b}"
                            : '`${symbol[0]}"${c}"${symbol[1]}"`',
                        color: labelColor,
                        fontSize: 9,
                    },
                    center: chartType === "pie" || chartType === "doughnut"
                        ? ["65%", "50%"]
                        : undefined,
                    funnelAlign: chartType === "funnel" ? "center" : undefined,
                },
            ],
        };
    };
    var rawData = chartData === null || chartData === void 0 ? void 0 : chartData.data;
    var labels = chartData === null || chartData === void 0 ? void 0 : chartData.labels;
    var check2D = isTwoDimensionalArray(rawData);
    /*const getChartOption2D = () => {
      const opened = rawData.map((d: any) => d[0]);
      const closed = rawData.map((d: any) => d[1]);
      return {
        title: {
          text: "Tickets Ouverts et Fermés par Période",
          left: "center",
        },
        tooltip: {
          trigger: "axis",
          axisPointer: { type: "shadow" },
        },
        legend: {
          data: ["Ouverts", "Fermés"],
          top: 30,
        },
        xAxis: {
          type: "category",
          data: labels,
          axisLabel: {
            rotate: 20,
          },
        },
        yAxis: {
          type: "value",
          name: "Nombre de tickets",
        },
        series: [
          {
            name: "Ouverts",
            type: "bar",
            data: opened,
            itemStyle: {
              color: "#2196F3",
            },
          },
          {
            name: "Fermés",
            type: "bar",
            data: closed,
            itemStyle: {
              color: "#FF5722",
            },
          },
        ],
      };
    };*/
    var getChartOption2D = function () {
        var opened = rawData.map(function (d) { return d[0]; });
        var closed = rawData.map(function (d) { return d[1]; });
        // Normalize chart type and determine orientation
        var isHorizontal = chartType === "horizontalBar";
        var normalizedType = chartType === "horizontalBar" ? "bar" : chartType;
        return {
            title: {
                text: "",
                left: "center",
            },
            tooltip: {
                trigger: "axis",
                axisPointer: { type: "shadow" },
            },
            legend: {
                data: ["Ouverts", "Fermés"],
                top: 30,
            },
            xAxis: isHorizontal
                ? {
                    type: "value",
                    name: "Nombre de tickets",
                }
                : {
                    type: "category",
                    data: labels,
                    axisLabel: {
                        rotate: 20,
                    },
                },
            yAxis: isHorizontal
                ? {
                    type: "category",
                    data: labels,
                }
                : {
                    type: "value",
                    name: "Nombre de tickets",
                },
            series: [
                {
                    name: "Ouverts",
                    type: normalizedType,
                    data: opened,
                    itemStyle: {
                        color: chartData === null || chartData === void 0 ? void 0 : chartData.colors[0],
                    },
                },
                {
                    name: "Fermés",
                    type: normalizedType,
                    data: closed,
                    itemStyle: {
                        color: chartData === null || chartData === void 0 ? void 0 : chartData.colors[1],
                    },
                },
            ],
        };
    };
    var optionToUse = check2D
        ? getChartOption2D()
        : chartType === "bar" || chartType === "horizontalBar"
            ? getChartBarOption()
            : getChartOption();
    return (_jsx(_Fragment, { children: ((_b = chartData === null || chartData === void 0 ? void 0 : chartData.data) === null || _b === void 0 ? void 0 : _b.length) > 0 ? (_jsx("div", __assign({ className: "overflow-auto" }, { children: _jsx("div", __assign({ className: "overflow-x-auto" }, { children: chartType === "funnelGraph" ? (_jsx(FunnelGraphComponent, { chartData: chartData, containerId: "funnel-".concat(Date.now()) }, "funnel-graph")) : chartType === "texte" ? (_jsx("div", __assign({ className: "flex" }, { children: check2D
                        ? chartData === null || chartData === void 0 ? void 0 : chartData.labels.map(function (label, i) { return (_jsx("div", __assign({ className: "w-[400px] p-3 rounded-xl" }, { children: _jsxs("div", __assign({ style: { background: chartData.colors[i] }, className: " p-6 rounded-lg text-white flex flex-col items-center" }, { children: [_jsx("span", __assign({ className: "mt-2 text-md font-bold" }, { children: "Tickets Ouverts/Ferm\u00E9s" })), _jsxs("span", __assign({ className: "text-3xl font-bold" }, { children: [chartData === null || chartData === void 0 ? void 0 : chartData.data[i][0], "/", chartData === null || chartData === void 0 ? void 0 : chartData.data[i][1]] })), _jsx("span", __assign({ className: "mt-2 text-sm" }, { children: label }))] })) }), label)); })
                        : chartData === null || chartData === void 0 ? void 0 : chartData.labels.map(function (label, i) { return (_jsx("div", __assign({ className: "w-[400px] p-3 rounded-xl" }, { children: _jsxs("div", __assign({ style: { background: chartData.colors[i] }, className: " p-6 rounded-lg text-white flex flex-col items-center" }, { children: [_jsxs("span", __assign({ className: "text-3xl font-bold" }, { children: [symbol[0], chartData === null || chartData === void 0 ? void 0 : chartData.data[i], symbol[1]] })), _jsx("span", __assign({ className: "mt-2 text-sm" }, { children: chartData === null || chartData === void 0 ? void 0 : chartData.tooltips[i] }))] })) }), label)); }) }))) : (_jsx(ReactECharts, { option: optionToUse, ref: chartRef, style: {
                        width: "100%",
                        height: "400px",
                        //height: "250px",
                        //width: "50%",
                        minWidth: "550px",
                    } }, "".concat(chartType, "-").concat(JSON.stringify(check2D)))) })) }))) : (_jsx("div", __assign({ className: "w-full p-5" }, { children: "No result" }))) }));
}
export default Chart;
